/**
 * Web Push flows: browser subscription lifecycle + the small API client.
 * No DOM here — main.ts/state.ts own rendering, this owns side effects.
 */
import type { Locale } from './i18n';

export type Frequency = 'occasionally' | 'regularly' | 'frequently' | 'regret';

const TOKEN_KEY = 'un_token';
const LOCALE_KEY = 'un_locale';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage unavailable (private mode, blocked storage) — UX degrades to "always idle", not fatal. */
  }
}

export function getStoredLocale(): string | null {
  try {
    return localStorage.getItem(LOCALE_KEY);
  } catch {
    return null;
  }
}

export function setStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* ignore */
  }
}

export function supportsPush(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iOS Safari only supports Web Push from an installed (Home Screen) PWA. */
export function needsIosInstall(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
  if (!isIos) return false;
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return !isStandalone;
}

function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init.headers },
  });
}

export async function fetchVapidPublicKey(): Promise<string> {
  const res = await apiFetch('/api/config');
  if (!res.ok) throw new Error('config_fetch_failed');
  const data = (await res.json()) as { vapidPublicKey: string };
  return data.vapidPublicKey;
}

async function getOrCreateServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

export interface EnableResult {
  token: string;
  frequency: Frequency;
  locale: Locale;
}

/** Full enable flow: permission, browser subscription, then register with the backend. */
export async function enablePush(frequency: Frequency, locale: Locale): Promise<EnableResult> {
  // Request permission first, before any other awaited work: WebKit ties the
  // permission prompt to the tap's user-activation window, and iOS Safari
  // (including installed PWAs) can drop that window across even one prior
  // `await` — e.g. service worker registration — causing this call to throw
  // instead of resolving to 'denied'.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const err = new Error('permission_denied');
    err.name = 'PermissionDeniedError';
    throw err;
  }

  const registration = await getOrCreateServiceWorkerRegistration();
  const vapidPublicKey = await fetchVapidPublicKey();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const res = await apiFetch('/api/subscriptions', {
    method: 'POST',
    body: JSON.stringify({ subscription: subscription.toJSON(), frequency, locale, timezone }),
  });
  if (!res.ok) throw new Error(`subscribe_failed_${res.status}`);
  const data = (await res.json()) as EnableResult;
  setStoredToken(data.token);
  setStoredLocale(data.locale);
  return data;
}

export interface CurrentSubscription {
  frequency: Frequency;
  locale: Locale;
}

/** Reconciles localStorage token + browser subscription + backend state. Returns null if there's no active subscription. */
export async function loadCurrentSubscription(): Promise<CurrentSubscription | null> {
  const token = getStoredToken();
  if (!token || !supportsPush()) return null;

  const res = await apiFetch('/api/subscriptions/current', { headers: { authorization: `Bearer ${token}` } });
  if (res.status === 404) {
    setStoredToken(null);
    return null;
  }
  if (!res.ok) throw new Error(`current_fetch_failed_${res.status}`);

  // Confirm the browser itself still has a live push subscription; if not,
  // the backend row is orphaned (e.g. the user cleared site data) — tell the
  // backend to drop it and treat this browser as unsubscribed.
  const registration = await navigator.serviceWorker.getRegistration('/');
  const browserSub = await registration?.pushManager.getSubscription();
  if (!browserSub) {
    await apiFetch('/api/subscriptions/current', { method: 'DELETE', headers: { authorization: `Bearer ${token}` } }).catch(() => {});
    setStoredToken(null);
    return null;
  }

  return (await res.json()) as CurrentSubscription;
}

export async function updatePreferences(patch: { frequency?: Frequency; locale?: Locale }): Promise<CurrentSubscription> {
  const token = getStoredToken();
  if (!token) throw new Error('no_token');
  const res = await apiFetch('/api/subscriptions/current', {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`update_failed_${res.status}`);
  if (patch.locale) setStoredLocale(patch.locale);
  return (await res.json()) as CurrentSubscription;
}

export async function disablePush(): Promise<void> {
  const token = getStoredToken();
  if (token) {
    const res = await apiFetch('/api/subscriptions/current', {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    }).catch(() => null);
    // Only tear down the browser-side subscription once the server has
    // confirmed the row is gone (or was already gone) — unsubscribing first
    // would orphan the backend row forever if the DELETE then failed, since
    // nothing would be left to retry it with.
    if (!res || (!res.ok && res.status !== 404)) return;
  }
  const registration = await navigator.serviceWorker.getRegistration('/');
  const browserSub = await registration?.pushManager.getSubscription();
  if (browserSub) await browserSub.unsubscribe().catch(() => {});
  setStoredToken(null);
}
